import type { DoiSoatKy, DoiSoatNguoi, LoaiThueTncn, VaiTroChia } from '@qlk/shared';
import {
  VAI_TRO_CHIA,
  VAI_TRO_CHIA_LABEL,
  dinhDangVnd,
  khoangNgayVn,
  thangCuaQuy,
} from '@qlk/shared';
import { useState } from 'react';

import { useKyChung } from '../../../layout/kyChung.js';
import {
  DanhSachSkeleton,
  EmptyState,
  Nut,
  OChon,
  PageHeader,
  SegmentedControl,
  StatStrip,
  ToolbarBang,
  dinhDangTien,
  type OChiSo,
} from '../../../ui/index.js';
import { duongDanXuatExcelDoiSoat, useChotDoiSoat, useChotTatCa, useDoiSoat } from '../api.js';
import { BangDoiSoatKenh } from '../components/BangDoiSoatKenh.js';
import { BangDoiSoatNguoi } from '../components/BangDoiSoatNguoi.js';
import { HopDieuChinh } from '../components/HopDieuChinh.js';
import { HopMoLai } from '../components/HopMoLai.js';
import { HopThanhToan } from '../components/HopThanhToan.js';
import { KhungTheoNhom } from '../components/KhungTheoNhom.js';
import { ThanhQuyTrinh } from '../components/ThanhQuyTrinh.js';

const CACH_XEM = [
  { value: 'nguoi', label: 'Theo người' },
  { value: 'nhom', label: 'Theo nhóm' },
  { value: 'kenh', label: 'Theo kênh' },
] as const;

type CachXem = (typeof CACH_XEM)[number]['value'];

/** `2026-10` → `Tháng 10/2026`. */
function nhanThang(period: string): string {
  const [nam, thang] = period.split('-');
  return `Tháng ${thang ?? '?'}/${nam ?? '?'}`;
}

/** Dòng phụ đầu trang: kỳ, khoảng ngày, và luật "chốt là đóng băng". */
function dongPhu(kq: DoiSoatKy | undefined, kyDangChon: string): string {
  if (!kq) return nhanThang(kyDangChon);

  if (kq.quy) {
    return `Quý ${kq.quy.replace('-Q', '/')} · gộp ${kq.cacKy
      .map(nhanThang)
      .join(', ')} · chỉ xem, chốt và thanh toán làm theo từng tháng`;
  }

  return `${nhanThang(kq.period)} · ${khoangNgayVn(kq.kyTu, kq.kyDen)} · đã chốt thì số liệu không đổi theo dữ liệu nguồn`;
}

/**
 * Đối soát nhân sự — chỉ `team:admin` mở được.
 *
 * Trang không tính gì: nó hiển thị kết quả `GET /team/doi-soat`, nơi máy chủ đã
 * gọi đúng hàm chia của bước 1. Mọi con số trên đây là số **đang hiệu lực**:
 * ảnh chụp với người đã chốt, tính trực tiếp với người còn nháp.
 *
 * ## Đổi ở bước 3/3
 *
 * - Kỳ lấy từ **ô chọn chung** ở thanh trên (`?ky=`), bỏ `OChon` "Kỳ" trong
 *   thân trang. Kiểu `thang` nên ô có cả 12 tháng và 4 quý gần nhất.
 * - Quy trình **Nháp → Đã chốt → Đã thanh toán** thành một thanh ba bước riêng
 *   (`ThanhQuyTrinh`); dải chỉ số còn bốn ô tiền thuần.
 * - Hai đoạn chú thích ở chân trang chuyển vào tooltip (?) của tiêu đề: chúng
 *   là luật cần biết **một lần**, không phải thứ đọc lại mỗi lần mở trang.
 */
export function TrangDoiSoatNhanSu() {
  const kyChung = useKyChung();
  /* Ô chung nhận cả `YYYY-MM` và `YYYY-Qn`; quý là chế độ chỉ xem của server. */
  const ky = kyChung.laQuy ? { quy: kyChung.giaTri } : { period: kyChung.giaTri };

  const doiSoat = useDoiSoat(ky);
  const chot = useChotDoiSoat();
  const chotTatCa = useChotTatCa();

  const [cachXem, setCachXem] = useState<CachXem>('nguoi');
  const [locNhom, setLocNhom] = useState('');
  const [locVaiTro, setLocVaiTro] = useState('');

  /*
   * Hộp thoại giữ **userId**, không giữ cả bản ghi: thêm một khoản điều chỉnh
   * rồi mà hộp vẫn hiện danh sách cũ thì người dùng tưởng nó không lưu được.
   * Lấy lại từ `kq.nguoi` mỗi lần render là luôn khớp dữ liệu vừa tải.
   */
  const [idDieuChinh, setIdDieuChinh] = useState<string | null>(null);
  const [idMoLai, setIdMoLai] = useState<string | null>(null);
  const [idThanhToan, setIdThanhToan] = useState<string | null>(null);

  const kq = doiSoat.data;
  const chiXem = kq?.chiXem ?? false;
  /** Kỳ dùng cho mọi thao tác: xem quý thì không có thao tác nào. */
  const kyThaoTac =
    kq?.period ?? (kyChung.laQuy ? (thangCuaQuy(kyChung.giaTri)[0] ?? '') : kyChung.giaTri);

  const dangLoc = locNhom !== '' || locVaiTro !== '';

  const timNguoi = (id: string | null) =>
    id === null ? null : ((kq?.nguoi ?? []).find((n) => n.userId === id) ?? null);

  /** Phiên bản đang hiệu lực của một người — nơi giữ tỷ giá và diện thuế. */
  const banHieuLuc = (n: DoiSoatNguoi) => n.phienBan.find((b) => b.hieuLuc) ?? null;
  const hopDieuChinh = timNguoi(idDieuChinh);
  const hopMoLai = timNguoi(idMoLai);
  const hopThanhToan = timNguoi(idThanhToan);

  const khopNguoi = (n: DoiSoatNguoi) =>
    (locNhom === '' || n.dong.some((d) => (d.teamId ?? '') === locNhom)) &&
    (locVaiTro === '' || n.vaiTro.includes(locVaiTro as VaiTroChia));

  const nguoiHien = (kq?.nguoi ?? []).filter(khopNguoi);
  const nhomHien = (kq?.theoNhom ?? []).filter(
    (n) => locNhom === '' || (n.teamId ?? '') === locNhom,
  );
  const kenhHien = (kq?.theoKenh ?? []).filter(
    (k) => locNhom === '' || (k.teamId ?? '') === locNhom,
  );

  /** Tổng VND, chỉ gồm người đã nhập tỷ giá. `null` khi chưa ai có. */
  const tongVnd = (() => {
    const co = (kq?.nguoi ?? [])
      .map((n) => banHieuLuc(n)?.phaiTraVnd ?? null)
      .filter((x): x is number => x !== null);
    return co.length === 0 ? null : co.reduce((t, x) => t + x, 0);
  })();

  const soNhap = kq?.tongQuyTrinh.demNhap ?? 0;

  const oChiSo: OChiSo[] = kq
    ? [
        {
          nhan: `Phần chia nhân sự · ${nhanThang(kq.period).toLowerCase()}`,
          giaTri: dinhDangTien(kq.tongQuyTrinh.phanChia),
          phu: `${String(kq.tong.soNguoi)} người · ${String(kq.tong.soKenh)} kênh`,
        },
        {
          nhan: `Điều chỉnh · ${nhanThang(kq.period).toLowerCase()}`,
          giaTri: dinhDangTien(kq.tongQuyTrinh.dieuChinh),
          phu: 'thưởng / phạt / tạm ứng / khấu trừ',
        },
        {
          nhan: `Phải trả · ${nhanThang(kq.period).toLowerCase()}`,
          giaTri: dinhDangTien(kq.tongQuyTrinh.phaiTra),
          phu: `đã trả ${dinhDangTien(kq.tongQuyTrinh.daTra)} · còn ${dinhDangTien(kq.tongQuyTrinh.conLai)}`,
        },
        {
          nhan: `Phải trả (VND) · ${nhanThang(kq.period).toLowerCase()}`,
          /* Chưa ai nhập tỷ giá thì in `—`: một số 0 ở đây đọc thành "không
             phải trả đồng nào", trong khi sự thật là "chưa quy đổi". */
          giaTri: tongVnd === null ? '—' : dinhDangVnd(tongVnd),
          phu: tongVnd === null ? 'nhập tỷ giá khi thanh toán' : 'chỉ gồm người đã nhập tỷ giá',
        },
      ]
    : [];

  return (
    <>
      <PageHeader
        title="Đối soát nhân sự"
        phu={dongPhu(kq, kyChung.giaTri)}
        giaiThich={
          'Chốt là đóng băng: sau đó sửa dữ liệu nguồn không làm đổi số đã chốt, trang chỉ cảnh báo lệch. ' +
          'Trang chỉ gồm kênh liên quan tới nhân sự — kênh thuộc một nhóm, hoặc có quản lý/editor được chia trong kỳ; ' +
          'kênh không thuộc nhóm nào và không có ai được chia không sinh ra khoản phải trả nên không hiện ở đây. ' +
          'Bộ lọc chỉ chọn dòng nào hiện ra: số của mỗi người vẫn là tổng cả kỳ, gồm cả kênh thuộc nhóm khác.'
        }
        actions={
          <>
            <Nut
              kieu="phu"
              disabled={chiXem}
              onClick={() => {
                window.location.href = duongDanXuatExcelDoiSoat(kyThaoTac);
              }}
            >
              Xuất Excel
            </Nut>
            {!chiXem && soNhap > 0 && (
              <Nut
                kieu="chinh"
                dangChay={chotTatCa.isPending}
                onClick={() => {
                  chotTatCa.mutate({ period: kyThaoTac });
                }}
              >
                Chốt tất cả nháp ({soNhap})
              </Nut>
            )}
          </>
        }
      />

      {doiSoat.isError ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được kỳ đối soát"
            description={doiSoat.error.message}
            action={
              <Nut
                kieu="phu"
                onClick={() => {
                  void doiSoat.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        </div>
      ) : doiSoat.isPending || !kq ? (
        <DanhSachSkeleton />
      ) : (
        <>
          <div className="mb-4">
            <ThanhQuyTrinh
              aria-label={`Quy trình ${nhanThang(kq.period).toLowerCase()}`}
              buoc={[
                {
                  nhan: 'Nháp',
                  so: kq.tongQuyTrinh.demNhap,
                  tone: 'neutral',
                  moTa:
                    kq.tongQuyTrinh.demNhap === 0
                      ? 'Không còn ai chờ chốt trong kỳ.'
                      : 'Số còn tính lại theo dữ liệu nguồn. Chốt để đóng băng.',
                },
                {
                  nhan: 'Đã chốt',
                  so: kq.tongQuyTrinh.demDaChot,
                  tone: 'info',
                  moTa:
                    kq.tongQuyTrinh.demLech > 0
                      ? `${String(kq.tongQuyTrinh.demLech)} người lệch so với dữ liệu nguồn hiện tại.`
                      : 'Số đã đóng băng, chờ chuyển tiền.',
                },
                {
                  nhan: 'Đã thanh toán',
                  so: kq.tongQuyTrinh.demDaThanhToan,
                  tone: 'ok',
                  moTa: 'Tiền đã chuyển, không mở lại được — sai sót xử lý ở kỳ sau.',
                },
              ]}
            />
          </div>

          <div className="mb-4">
            <StatStrip aria-label={`Chỉ số ${nhanThang(kq.period).toLowerCase()}`} o={oChiSo} />
          </div>

          {(chot.isError || chotTatCa.isError) && (
            <p role="alert" className="mb-3 text-bang text-bad-chu">
              {chot.error?.message ?? chotTatCa.error?.message}
            </p>
          )}

          {/*
            "Chốt tất cả" **bỏ qua** người còn kênh chưa đặt ngày vận hành chứ
            không làm cả lượt thất bại. Phải nói ra còn ai, nếu không người dùng
            tưởng đã chốt xong hết.
          */}
          {(chotTatCa.data?.boQua.length ?? 0) > 0 && (
            <div className="mb-3 rounded-o bg-warn-nen px-3 py-2 text-bang text-warn-chu">
              <p>
                <strong>
                  Bỏ qua {chotTatCa.data?.boQua.length} người — kênh chưa đặt ngày bắt đầu vận hành.
                </strong>{' '}
                Chi phí của các kênh đó đang bị trừ toàn bộ nên số chưa đáng tin để đóng băng.
              </p>
              <ul className="mt-1 list-disc pl-5">
                {chotTatCa.data?.boQua.map((b) => (
                  <li key={b.userId}>
                    {b.tenNguoi}: {b.kenhThieuNgayVanHanh.map((k) => k.tenKenh).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ToolbarBang
            trai={
              <SegmentedControl
                aria-label="Cách xem"
                muc={CACH_XEM.map((c) => ({ giaTri: c.value, nhan: c.label }))}
                giaTri={cachXem}
                onChange={(v) => {
                  setCachXem(v as CachXem);
                }}
              />
            }
            phai={
              <>
                <OChon
                  label="Nhóm"
                  value={locNhom}
                  onChange={setLocNhom}
                  options={[
                    { value: '', label: 'Mọi nhóm' },
                    ...(kq.theoNhom ?? []).map((n) => ({
                      value: n.teamId ?? '',
                      label: n.tenNhom,
                    })),
                  ]}
                />
                <OChon
                  label="Vai trò"
                  value={locVaiTro}
                  onChange={setLocVaiTro}
                  options={[
                    { value: '', label: 'Mọi vai trò' },
                    ...VAI_TRO_CHIA.map((v) => ({ value: v, label: VAI_TRO_CHIA_LABEL[v] })),
                  ]}
                />
              </>
            }
          />

          {dangLoc && (
            <p className="mb-3 text-phu text-chu-phu">
              Bộ lọc chỉ chọn <strong>dòng nào hiện ra</strong>; số của mỗi người và dải chỉ số ở
              trên vẫn là tổng cả kỳ.
            </p>
          )}

          {cachXem === 'nguoi' && (
            <BangDoiSoatNguoi
              nguoi={nguoiHien}
              chiXem={chiXem}
              dangChay={chot.isPending}
              onDieuChinh={(n) => {
                setIdDieuChinh(n.userId);
              }}
              onChot={(n) => {
                chot.mutate({ period: kyThaoTac, userId: n.userId });
              }}
              onMoLai={(n) => {
                setIdMoLai(n.userId);
              }}
              onThanhToan={(n) => {
                setIdThanhToan(n.userId);
              }}
            />
          )}

          {cachXem === 'nhom' && <KhungTheoNhom theoNhom={nhomHien} />}

          {cachXem === 'kenh' && (
            <BangDoiSoatKenh theoKenh={kenhHien} hienCotKy={kq.cacKy.length > 1} />
          )}
        </>
      )}

      {hopDieuChinh && (
        <HopDieuChinh
          period={kyThaoTac}
          userId={hopDieuChinh.userId}
          tenNguoi={hopDieuChinh.tenNguoi}
          dangCo={hopDieuChinh.khoanDieuChinh.filter((d) => d.period === kyThaoTac)}
          onDong={() => {
            setIdDieuChinh(null);
          }}
        />
      )}

      {hopMoLai && (
        <HopMoLai
          period={kyThaoTac}
          userId={hopMoLai.userId}
          tenNguoi={hopMoLai.tenNguoi}
          phaiTra={hopMoLai.phaiTra}
          onDong={() => {
            setIdMoLai(null);
          }}
        />
      )}

      {hopThanhToan && (
        <HopThanhToan
          period={kyThaoTac}
          userId={hopThanhToan.userId}
          tenNguoi={hopThanhToan.tenNguoi}
          phaiTra={hopThanhToan.phaiTra}
          loaiThue={(banHieuLuc(hopThanhToan)?.loaiThue as LoaiThueTncn | null) ?? null}
          tyGiaDaCo={banHieuLuc(hopThanhToan)?.tyGiaVnd ?? null}
          onDong={() => {
            setIdThanhToan(null);
          }}
        />
      )}
    </>
  );
}
